import { Breadcrumbs, Grid, Typography, Link, Box, Chip, Tooltip, Skeleton } from "@mui/material"
import { useSearchParams } from "react-router-dom";
import { IMarketplace } from "../../../../interfaces/IMarketplace";
import { ConteudoContainer, PageContainer, PainelContainer } from "../../styles";
import { NavegacaoSecoes, SecaoNavegacao } from "../../components/NavegacaoSecoes";
import { PainelSecao } from "../../components/PainelSecao";
import { LinhaDado } from "../../components/LinhaDado";
import { StatusMarketplacePaper } from "../../components/StatusMarketplacePaper";
import { TipoMarketplaceCor, TipoMarketplaceDescricao, TipoMarketplaceTitulo } from "../../../../enum/TipoMarketplace";
import { PersonalizacaoMarketplacePaper } from "../../components/PersonalizacaoPaper";
import { HorariosMarketplacePaper } from "../../components/HorariosMarketplacePaper";
import { BadgeOutlined, DashboardOutlined, ExtensionOutlined, ScheduleOutlined, TableRestaurantOutlined } from "@mui/icons-material";
import { PixelMetaContainer } from "../../components/PixelMetaContainer";
import { LogRocketContainer } from "../../components/LogRocketContainer";
import { CabecalhoMarketplace } from "../../components/CabecalhoMarketplace";
import { SecaoConfiguracao } from "../../../../../core/components/SecaoConfiguracao";
import { QRCodesMesasPaper } from "../../components/QRCodesMesasPaper";
import { ConfiguracaoMesaPaper } from "../../components/ConfiguracaoMesaPaper";

interface MesaQRCodeContainerProps {
    marketplace?: IMarketplace
    handleRefresh: () => void,
    loadingFetch: boolean
}

type SecaoId = "geral" | "mesas" | "funcionamento" | "integracoes";

const secoes: SecaoNavegacao<SecaoId>[] = [
    { id: "geral", titulo: "Visão geral", icone: <DashboardOutlined /> },
    { id: "mesas", titulo: "Mesas e QR Codes", icone: <TableRestaurantOutlined /> },
    { id: "funcionamento", titulo: "Funcionamento", icone: <ScheduleOutlined /> },
    { id: "integracoes", titulo: "Integrações", icone: <ExtensionOutlined /> },
];

/**
 * QR Code por mesa: mesmo cardápio do online, sem entrega/pagamento — o cliente pede por
 * rodadas na conta da mesa e o atendente fecha pelo PDV.
 */
export const MesaQRCodeContainer = ({ marketplace, handleRefresh, loadingFetch }: MesaQRCodeContainerProps) => {
    // A seção fica na URL para não ser perdida quando a página é recarregada
    // depois de salvar uma configuração (handleRefresh remonta o container).
    const [parametros, setParametros] = useSearchParams();
    const secaoParametro = parametros.get("secao") as SecaoId | null;
    const secaoAtiva: SecaoId = secoes.some(secao => secao.id === secaoParametro)
        ? secaoParametro as SecaoId
        : "geral";

    const setSecaoAtiva = (secao: SecaoId) => {
        const novosParametros = new URLSearchParams(parametros);
        novosParametros.set("secao", secao);
        setParametros(novosParametros, { replace: true });
    }

    return (
        <PageContainer>
            <Breadcrumbs aria-label="breadcrumb">
                <Link color="primary" href="/marketplace">
                    Marketplaces
                </Link>
                {marketplace && (
                    <Typography color="text.primary">{marketplace.nome}</Typography>
                )}
            </Breadcrumbs>
            {!marketplace ? (
                <Box sx={{ mt: 3 }}>
                    <Skeleton variant="rounded" height={220} sx={{ borderRadius: "16px" }} />
                    <Skeleton variant="rounded" height={320} sx={{ borderRadius: "16px", mt: 3 }} />
                </Box>
            ) : (
                <>
                    <Box sx={{ mt: 2 }}>
                        <CabecalhoMarketplace marketplace={marketplace} />
                    </Box>
                    <ConteudoContainer>
                        <NavegacaoSecoes
                            secoes={secoes}
                            secaoAtiva={secaoAtiva}
                            onChange={setSecaoAtiva}
                        />
                        <PainelContainer>
                            <PainelSecao ativo={secaoAtiva === "geral"}>
                                <Grid item xs={12} md={6}>
                                    <StatusMarketplacePaper marketplace={marketplace} />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <SecaoConfiguracao
                                        titulo="Dados"
                                        descricao="Informações apresentadas aos clientes"
                                        icone={<BadgeOutlined />}
                                    >
                                        <LinhaDado rotulo="Nome público">
                                            <Typography variant="body2" fontWeight={500}>
                                                {marketplace.configuracao?.nomePublico ?? "-"}
                                            </Typography>
                                        </LinhaDado>
                                        <LinhaDado rotulo="Nome interno">
                                            <Typography variant="body2" fontWeight={500}>
                                                {marketplace.nome ?? "-"}
                                            </Typography>
                                        </LinhaDado>
                                        <LinhaDado rotulo="Tipo">
                                            <Tooltip title={TipoMarketplaceDescricao[marketplace.tipo]}>
                                                <Chip
                                                    size="small"
                                                    label={TipoMarketplaceTitulo[marketplace.tipo]}
                                                    style={{ background: TipoMarketplaceCor[marketplace.tipo], color: "white" }}
                                                />
                                            </Tooltip>
                                        </LinhaDado>
                                        <LinhaDado rotulo="Descrição">
                                            <Typography variant="body2" fontWeight={500}>
                                                {marketplace.descricao || "-"}
                                            </Typography>
                                        </LinhaDado>
                                    </SecaoConfiguracao>
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <PersonalizacaoMarketplacePaper marketplace={marketplace} />
                                </Grid>
                            </PainelSecao>

                            <PainelSecao ativo={secaoAtiva === "mesas"}>
                                <Grid item xs={12}>
                                    <QRCodesMesasPaper marketplace={marketplace} refresh={handleRefresh} />
                                </Grid>
                                <Grid item xs={12}>
                                    <ConfiguracaoMesaPaper marketplace={marketplace} refresh={handleRefresh} />
                                </Grid>
                            </PainelSecao>

                            <PainelSecao ativo={secaoAtiva === "funcionamento"}>
                                <Grid item xs={12} md={6}>
                                    <HorariosMarketplacePaper refresh={handleRefresh} marketplace={marketplace} />
                                </Grid>
                            </PainelSecao>

                            <PainelSecao ativo={secaoAtiva === "integracoes"}>
                                <Grid item xs={12} md={6}>
                                    <PixelMetaContainer isLoading={loadingFetch} refresh={handleRefresh} marketplace={marketplace} />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <LogRocketContainer isLoading={loadingFetch} refresh={handleRefresh} marketplace={marketplace} />
                                </Grid>
                            </PainelSecao>
                        </PainelContainer>
                    </ConteudoContainer>
                </>
            )}
        </PageContainer>
    )
}
